import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { usePageSellerId } from '@dashboard/hooks/usePage';
import { fetcher } from '../../services/request';
import logException from '../../services/logException';
import { aiPageResetKey, pagesKey, sellerProductsKey } from '../../lib/queryKeys';

/**
 * Generate a page from AI description
 * Pass sellerId and token explicitly to avoid closure issues with async auth state
 */
export function useGeneratePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ description, type, theme, collectAddress, sellerId, token }) => {
      if (!sellerId) {
        throw new Error('Seller ID not available');
      }
      return await fetcher(`/sellers/${sellerId}/ai/generate-page`, token, {
        method: 'POST',
        body: JSON.stringify({ description, type, theme, collectAddress }),
      });
    },
    onSuccess: () => {
      // Invalidate pages list to show the new page
      queryClient.invalidateQueries({ queryKey: pagesKey });
    },
  });
}

// Create with AI starts from a blank page that the AI editor then builds.
export function useCreateBlankAIPage() {
  const queryClient = useQueryClient();
  const { sellerId, token } = useAuth();

  return useMutation({
    mutationFn: ({ type, theme, collectAddress }) =>
      fetcher(`/sellers/${sellerId}/ai/blank-page`, token, {
        method: 'POST',
        body: JSON.stringify({ type, theme, collectAddress }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
  });
}

export function useAIPageResetStatus(pageId) {
  const { token } = useAuth();
  const sellerId = usePageSellerId();

  return useQuery({
    queryKey: aiPageResetKey(pageId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/${pageId}/ai/reset`, token),
    enabled: Boolean(sellerId && pageId),
  });
}

export function useResetAIPage(pageId) {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const sellerId = usePageSellerId();

  return useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${pageId}/ai/reset`, token, { method: 'POST' }),
    // Also on an error: a refusal means a purchase came in, and the reset status should catch up.
    onSettled: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
    // The order bump picker lists the seller's products, and the reset deletes the page's own.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sellerProductsKey(sellerId) }),
  });
}

// The requester's latest AI editor conversation on the page; throws on an API error or a reply that is not a history.
export async function fetchAIChatHistory({ sellerId, pageId, token }) {
  const [data, { AiChatHistoryResponseValidationSchema, AiChatTurnValidationSchema, z }] =
    await Promise.all([
      fetcher(`/sellers/${sellerId}/pages/${pageId}/ai/chat/history`, token),
      // On demand: @repo/schemas brings zod, which the dashboard's main bundle does not otherwise load.
      import('@repo/schemas'),
    ]);
  const history = AiChatHistoryResponseValidationSchema.extend({
    turns: z.array(z.unknown()),
  }).parse(data);
  // A turn's changes and points are stored loosely, so one that no longer parses is dropped, not the chat.
  const results = history.turns.map(turn => AiChatTurnValidationSchema.safeParse(turn));
  const failed = results.filter(result => !result.success);
  if (failed.length > 0) {
    logException(
      new Error(`Dropped ${failed.length} saved AI chat turn(s) that did not parse`, {
        cause: failed[0].error,
      }),
    );
  }
  return {
    conversationId: history.conversationId,
    turns: results.filter(result => result.success).map(result => result.data),
  };
}
